import { parseISO } from "date-fns"
import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"

import Button from "src/components/Button"
import Form from "src/components/Form"
import Modal from "src/components/Modal"

const NoteModal = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  note,
  date,
  isLoading,
  isDeleting,
}) => {
  const [text, setText] = useState("")
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const textareaRef = useRef(null)

  // Parse a date string (YYYY-MM-DD) to Date object
  const parseDate = (dateStr) => {
    if (!dateStr) return null
    // Handle YYYY-MM-DD format
    if (dateStr.includes("-")) {
      return parseISO(dateStr)
    }
    // Handle MM/DD/YYYY format
    return new Date(dateStr)
  }

  // Reset form when modal opens or note changes
  useEffect(() => {
    if (isOpen) {
      setText(note?.text || "")
      // For editing, use the note's dates; for new notes, use the current date
      const defaultDate = parseDate(date)
      setStartDate(note?.startDate ? parseDate(note.startDate) : defaultDate)
      setEndDate(note?.endDate ? parseDate(note.endDate) : defaultDate)
    }
  }, [note, date, isOpen])

  const isEditing = !!note

  // Convert Date to MM/DD/YYYY format expected by the API
  const formatDateForApi = (dateObj) => {
    if (!dateObj) return ""
    const month = String(dateObj.getMonth() + 1).padStart(2, "0")
    const day = String(dateObj.getDate()).padStart(2, "0")
    const year = dateObj.getFullYear()
    return `${month}/${day}/${year}`
  }

  const handleSave = () => {
    onSave({
      text,
      startDate: formatDateForApi(startDate),
      endDate: formatDateForApi(endDate),
    })
  }

  // Ensure end date is not before start date
  const handleStartDateChange = (newDate) => {
    setStartDate(newDate)
    if (endDate && newDate > endDate) {
      setEndDate(newDate)
    }
  }

  const handleEndDateChange = (newDate) => {
    setEndDate(newDate)
  }

  const canSave = text.trim().length > 0 && startDate && endDate

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="small"
      initialFocusRef={textareaRef}
    >
      <Modal.Header title={isEditing ? "Edit Note" : "Add Note"} />
      <Modal.Body>
        <div className="space-y-4">
          {/* Date Range */}
          <div className="flex gap-4">
            <div className="flex-1">
              <Form.Label htmlFor="note-start-date">Start Date</Form.Label>
              <Form.DatePicker
                id="note-start-date"
                value={startDate}
                onChange={handleStartDateChange}
                maxDate={endDate}
                placeholderText="MM/DD/YYYY"
              />
            </div>
            <div className="flex-1">
              <Form.Label htmlFor="note-end-date">End Date</Form.Label>
              <Form.DatePicker
                id="note-end-date"
                value={endDate}
                onChange={handleEndDateChange}
                minDate={startDate}
                placeholderText="MM/DD/YYYY"
              />
            </div>
          </div>

          {/* Note Text */}
          <div>
            <Form.Label htmlFor="note-text">Note</Form.Label>
            <Form.Textarea
              ref={textareaRef}
              id="note-text"
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Enter your note..."
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex items-center justify-between">
          <div>
            {onDelete && (
              <Button
                variant="danger"
                onClick={onDelete}
                isLoading={isDeleting}
                disabled={isLoading}
              >
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isLoading || isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              isLoading={isLoading}
              disabled={!canSave || isDeleting}
            >
              {isEditing ? "Save" : "Add"}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

NoteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  note: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    text: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
  date: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  isDeleting: PropTypes.bool,
}

export default NoteModal
